/**
 * S/R Cluster Label
 *
 * One label per price cluster, drawn just left of the price axis.
 *
 * Collapsed it shows exactly two rows — the price, then the level count and the
 * single highest timeframe backing the cluster. Before this, each label spelled
 * out every timeframe present ("WD4H1H S 24"), which is what made a chart with
 * six timeframes enabled unreadable.
 *
 * Clicking expands it in place into a card carrying everything the two rows
 * drop: every timeframe in the cluster, the support/resistance split, the exact
 * price span, touches, strength, confluence and touch history.
 *
 * Touch history is grouped by day and then by level, each row carrying a count.
 * It used to be a flat list of individual timestamps, which is unreadable once
 * a level has touches in the hundreds. The counts are drawn from the engine's
 * last-10-timestamps-per-level window, so the card states how much of the
 * history it is actually showing rather than implying it is all of it.
 *
 * The label is its own click target, so the hit box is the rendered box by
 * construction — it cannot drift away from the text the way a canvas-drawn
 * label with separately-computed hit testing can.
 */

import { forwardRef } from 'react';
import { SRLevel, SRTimeframe } from '@shared/types';
import {
  SR_TIMEFRAME_CONFIG,
  highestSRTimeframe,
  buildSRExpandedFields,
  formatSRLabel,
  groupSRTouchHistory,
} from '../../store/srStore';

// ============================================================================
// Types
// ============================================================================

/** One rendered label: a cluster of S/R levels at effectively the same price. */
export interface SRLabelGroup {
  /** Stable cluster identity — see `srGroupKey`. */
  key: string;
  /** Y coordinate of the cluster, in CSS px. */
  yPosition: number;
  /** Every level in the cluster. Each keeps its own timeframe and its own line. */
  levels: SRLevel[];
  /** Row 1 — formatted cluster price. */
  priceText: string;
  /** Row 2 — summed touch count across the cluster. */
  count: number;
  /** Row 2 — the single highest timeframe present, e.g. `W`, `D`, `4H`. */
  timeframeLabel: string;
  /** Highest timeframe's colour, matching its price line. */
  color: string;
  /** Dominant side of the cluster. */
  levelType: 'support' | 'resistance';
  /** Mean price, kept numeric for Y-positioning. */
  price: number;
  /** How many of the last 5 candles traded through this level. */
  breachCount: number;
}

export interface SRLabelProps {
  group: SRLabelGroup;
  expanded: boolean;
  onToggle: (key: string) => void;
  /** Hide the cluster's top timeframe (the card's "Hide" button). */
  onHideTimeframe?: (tf: SRTimeframe) => void;
}

// ============================================================================
// Component
// ============================================================================

function breachGlyph(breachCount: number): string {
  if (breachCount > 1) return '🔴';
  if (breachCount === 1) return '🍑';
  return '🟢';
}

export const SRLabel = forwardRef<HTMLDivElement, SRLabelProps>(function SRLabel(
  { group, expanded, onToggle, onHideTimeframe },
  ref
) {
  return (
    <div
      ref={ref}
      data-testid={`sr-label-${group.key}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      className={`sr-label sr-label-${group.levelType}${expanded ? ' sr-label-expanded' : ''}`}
      style={{
        top: `${group.yPosition}px`,
        borderLeftColor: group.color,
      }}
      onClick={(e) => {
        // The chart's own click handler treats a click near the price axis as
        // "collapse whatever is open". Without this the toggle would fight it.
        e.stopPropagation();
        onToggle(group.key);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onToggle(group.key);
        }
      }}
      title={expanded ? undefined : 'Click for details'}
    >
      {expanded ? (
        <SRLabelDetail group={group} onHideTimeframe={onHideTimeframe} />
      ) : (
        <>
          <div className="sr-label-row sr-label-row-price">
            <span className="sr-label-breach">{breachGlyph(group.breachCount)}</span>
            <span className="sr-label-price">{group.priceText}</span>
          </div>
          <div className="sr-label-row sr-label-row-meta">
            <span className="sr-label-count">{group.count}</span>
            <span className="sr-label-sep">·</span>
            <span className="sr-label-tf" style={{ color: group.color }}>
              {group.timeframeLabel}
            </span>
          </div>
        </>
      )}
    </div>
  );
});

// ============================================================================
// Expanded card
// ============================================================================

function SRLabelDetail({ group, onHideTimeframe }: { group: SRLabelGroup; onHideTimeframe?: (tf: SRTimeframe) => void }) {
  const history = groupSRTouchHistory(group.levels);
  const topTf = highestSRTimeframe(group.levels);

  return (
    <>
      <div className="sr-detail-header">
        <span className="sr-detail-swatch" style={{ backgroundColor: group.color }} />
        <span className="sr-detail-price">{group.priceText}</span>
        <span className="sr-detail-tf" style={{ color: group.color }}>
          {group.timeframeLabel}
        </span>
        {onHideTimeframe && topTf && (
          <button
            type="button"
            className="sr-detail-hide"
            data-testid="sr-label-hide"
            title={`Hide all ${SR_TIMEFRAME_CONFIG[topTf].label} S/R levels (turn back on in the S/R panel)`}
            onClick={(e) => {
              e.stopPropagation();
              onHideTimeframe(topTf);
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            Hide {SR_TIMEFRAME_CONFIG[topTf].label}
          </button>
        )}
      </div>

      <div className="sr-detail-rows">
        {buildSRExpandedFields(group.levels).map((row) => (
          <div key={row.label} className="sr-detail-row">
            <span className="sr-detail-key">{row.label}</span>
            <span className="sr-detail-value">{row.value}</span>
          </div>
        ))}
      </div>

      {/* The per-timeframe breakdown is exactly what collapsing to one label
          hides, so it comes back whenever there is more than one level. */}
      {group.levels.length > 1 && (
        <>
          <div className="sr-detail-title">Levels in cluster</div>
          <div className="sr-detail-rows">
            {group.levels.map((level) => (
              <div key={`${level.price}-${level.timeframe}`} className="sr-detail-row">
                <span
                  className="sr-detail-key"
                  style={{ color: SR_TIMEFRAME_CONFIG[level.timeframe].color.slice(0, 7) }}
                >
                  {formatSRLabel(level)}
                </span>
                <span className="sr-detail-value">{level.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="sr-detail-title">Touch history</div>

      {/* The engine keeps only the last 10 timestamps per level while
          `touchCount` keeps counting, so across live data these rows account
          for about 3% of all touches. Saying so is the difference between a
          sample and a lie — without it "Aug 4 · 12" reads as the whole day. */}
      {history.truncated && history.sampledCount > 0 && (
        <div className="sr-detail-caption">
          last {history.sampledCount} of {history.totalTouchCount.toLocaleString('en-US')} touches
        </div>
      )}

      <div className="sr-detail-timestamps">
        {history.days.length > 0 ? (
          <>
            {history.days.map((day) => (
              <div key={day.dayKey} className="sr-touch-day">
                <div className="sr-touch-day-header">
                  <span className="sr-touch-day-label">{day.dayLabel}</span>
                  <span className="sr-touch-day-count">{day.count}</span>
                </div>
                {day.levels.map((level) => (
                  <div
                    key={`${level.price}-${level.timeframe}`}
                    className="sr-touch-level"
                  >
                    <span className="sr-touch-level-price">{level.price.toFixed(2)}</span>
                    <span className="sr-touch-level-tf" style={{ color: level.color }}>
                      {level.timeframeLabel}
                    </span>
                    <span className="sr-touch-level-count">{level.count}</span>
                  </div>
                ))}
              </div>
            ))}
            {/* The cap has to announce itself. A list that just stops reads as
                "those are all the touches", which is not true. */}
            {history.hiddenDays > 0 && (
              <div className="sr-touch-more">
                {`+ ${history.hiddenDays} earlier ${
                  history.hiddenDays === 1 ? 'day' : 'days'
                } (${history.hiddenCount})`}
              </div>
            )}
          </>
        ) : (
          <div className="sr-detail-empty">No timestamp data</div>
        )}
      </div>
    </>
  );
}
